// components/PodcastModerationQueue.tsx
// Podcast moderation queue for reviewing user-submitted episodes

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { StatsCard } from './StatsCard';
import {
    fetchPodcastModerationQueue,
    getPodcastModerationStats,
    flagForSeniorReview,
    rejectionTemplates,
    categoryInfo
} from '../services/podcastModeration';
import {
    approvePodcast,
    rejectPodcast,
    removePodcast
} from '../services/podcastModerationService';
import type {
    PodcastModerationQueueItem,
    PodcastModerationStats,
    RejectionCategory,
    PodcastReviewStatus
} from '../types';
import './PodcastModerationQueue.css';

export const PodcastModerationQueue: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [queue, setQueue] = useState<PodcastModerationQueueItem[]>([]);
    const [stats, setStats] = useState<PodcastModerationStats>({
        totalPending: 0,
        totalReviewedToday: 0,
        totalReviewedThisWeek: 0,
        totalReviewedThisMonth: 0,
        approvalRate: 0,
        averageReviewTimeMinutes: 0,
        queueBacklog: 0,
        rejectionReasons: {},
        episodesByCategory: {}
    });
    const [selectedItem, setSelectedItem] = useState<PodcastModerationQueueItem | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<PodcastReviewStatus>('pending');

    // Rejection modal state
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionCategory, setRejectionCategory] = useState<RejectionCategory>('inappropriate_language');
    const [rejectionReason, setRejectionReason] = useState('');

    // Audio player ref
    const audioRef = useRef<HTMLAudioElement>(null);


    useEffect(() => {
        // Update rejection reason template when category changes
        setRejectionReason(rejectionTemplates[rejectionCategory]);
    }, [rejectionCategory]);

    const loadQueue = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await fetchPodcastModerationQueue(50, statusFilter);
            setQueue(data);
        } catch (err: any) {
            console.error('Error loading queue:', err);
            setError('Failed to load podcast moderation queue');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    const loadStats = useCallback(async () => {
        try {
            const statsData = await getPodcastModerationStats();
            setStats(statsData);
        } catch (err: any) {
            console.error('Error loading stats:', err);
        }
    }, []);

    useEffect(() => {
        loadQueue();
        loadStats();
    }, [loadQueue, loadStats]);

    const handleApprove = async (item: PodcastModerationQueueItem) => {
        if (!currentUser?.email) {
            alert('You must be signed in to approve episodes');
            return;
        }

        if (!window.confirm(`Approve "${item.episode.title}" by ${item.episode.creatorName}?`)) {
            return;
        }

        try {
            setActionLoading(true);
            await approvePodcast(item.episode.id, {
                autoPublish: false,
                autoShareToBarkBoard: false
            });
            await loadQueue();
            await loadStats();
            setSelectedItem(null);
            alert('✅ Episode approved! User can now share it.');
        } catch (err: any) {
            console.error('Error approving episode:', err);
            alert('Failed to approve episode: ' + (err.message || 'Unknown error'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async (item: PodcastModerationQueueItem) => {
        if (!currentUser?.email) {
            alert('You must be signed in to reject episodes');
            return;
        }

        if (!rejectionReason.trim()) {
            alert('Please provide a rejection reason');
            return;
        }

        if (rejectionReason.trim().length < 10) {
            alert('Rejection reason must be at least 10 characters');
            return;
        }

        try {
            setActionLoading(true);
            // Map old categories to new ones
            const categoryMap: Record<string, any> = {
                'inappropriate_language': 'inappropriate_content',
                'off_topic_content': 'guideline_violation',
                'safety_concerns': 'guideline_violation',
                'quality_issues': 'audio_quality',
                'metadata_issues': 'other',
                'other': 'other'
            };
            const mappedCategory = categoryMap[rejectionCategory] || 'other';

            await rejectPodcast(
                item.episode.id,
                rejectionReason,
                mappedCategory
            );
            await loadQueue();
            await loadStats();
            setSelectedItem(null);
            setShowRejectModal(false);
            setRejectionReason('');
            alert('❌ Episode rejected. User will be notified.');
        } catch (err: any) {
            console.error('Error rejecting episode:', err);
            alert('Failed to reject episode: ' + (err.message || 'Unknown error'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleRemove = async (item: PodcastModerationQueueItem) => {
        if (!currentUser?.email) {
            alert('You must be signed in to remove episodes');
            return;
        }

        const reason = window.prompt(
            'This will permanently remove the episode for severe violations.\n\nEnter removal reason:'
        );

        if (!reason || !reason.trim()) {
            return;
        }

        if (reason.trim().length < 10) {
            alert('Removal reason must be at least 10 characters');
            return;
        }

        if (!window.confirm('⚠️ Are you sure you want to REMOVE this episode? This is for severe violations only.')) {
            return;
        }

        try {
            setActionLoading(true);
            await removePodcast(item.episode.id, reason);
            await loadQueue();
            await loadStats();
            setSelectedItem(null);
            alert('🗑️ Episode removed.');
        } catch (err: any) {
            console.error('Error removing episode:', err);
            alert('Failed to remove episode: ' + (err.message || 'Unknown error'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleFlagForSeniorReview = async (item: PodcastModerationQueueItem) => {
        if (!currentUser?.email) {
            alert('You must be signed in to flag episodes');
            return;
        }

        const reason = window.prompt('Flag this episode for senior review.\n\nReason:');

        if (!reason || !reason.trim()) {
            return;
        }

        try {
            setActionLoading(true);
            await flagForSeniorReview(item.episode.id, currentUser.email, reason);
            await loadQueue();
            await loadStats();
            setSelectedItem(null);
            alert('🚩 Episode flagged for senior review');
        } catch (err: any) {
            console.error('Error flagging episode:', err);
            alert('Failed to flag episode: ' + err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    const formatDate = (date: Date | null | undefined): string => {
        if (!date) return 'N/A';
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));

        if (hours < 1) return 'Just now';
        if (hours < 24) return `${hours}h ago`;
        if (hours < 48) return 'Yesterday';
        return date.toLocaleDateString();
    };

    const formatDuration = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getUserStatusBadge = (userStats: any) => {
        if (!userStats) return null;

        const { approvalRate, rejectedCount, removedCount } = userStats;

        if (removedCount > 0) {
            return <span className="user-badge removed">⚠️ Has removals</span>;
        }

        if (approvalRate < 0.3 && (userStats.approvedCount + rejectedCount) > 3) {
            return <span className="user-badge low-approval">⚠️ Low approval rate</span>;
        }

        if (userStats.totalEpisodes === 1) {
            return <span className="user-badge first-time">✨ First episode</span>;
        }

        return null;
    };

    return (
        <div className="podcast-moderation-container">
            <header className="podcast-moderation-header">
                <div className="header-content">
                    <div>
                        <h1>🎙️ Podcast Moderation</h1>
                        <p className="header-subtitle">Review user-submitted podcast episodes</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate('/dashboard')} className="back-button">
                            ← Back to Dashboard
                        </button>
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <div className="podcast-moderation-main">
                {/* Stats Section */}
                <section className="stats-section">
                    <h2>📊 Queue Stats</h2>
                    <div className="stats-grid">
                        <StatsCard
                            title="Pending"
                            value={stats.totalPending}
                            icon="⏳"
                            color="yellow"
                            subtitle="Awaiting review"
                        />
                        <StatsCard
                            title="Reviewed Today"
                            value={stats.totalReviewedToday}
                            icon="✅"
                            color="green"
                            subtitle="Episodes reviewed"
                        />
                        <StatsCard
                            title="Approval Rate"
                            value={`${stats.approvalRate}%`}
                            icon="📈"
                            color="blue"
                            subtitle="Overall approval"
                        />
                        <StatsCard
                            title="Backlog"
                            value={stats.queueBacklog}
                            icon="⚠️"
                            color="red"
                            subtitle="> 24 hours old"
                        />
                    </div>
                </section>

                {/* Filters Section */}
                <section className="filters-section">
                    <div className="filter-group">
                        <label>Status Filter:</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as PodcastReviewStatus)}
                            className="filter-select"
                        >
                            <option value="pending">Pending Review</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="flagged">Flagged</option>
                            <option value="removed">Removed</option>
                        </select>
                    </div>
                    <button onClick={() => { loadQueue(); loadStats(); }} className="refresh-button">
                        🔄 Refresh
                    </button>
                </section>

                {/* Queue Section */}
                <section className="queue-section">
                    <div className="queue-header">
                        <h2>📋 Episodes Queue ({queue.length})</h2>
                    </div>

                    {loading && (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Loading queue...</p>
                        </div>
                    )}

                    {error && (
                        <div className="error-state">
                            <p>{error}</p>
                            <button onClick={loadQueue} className="retry-button">Retry</button>
                        </div>
                    )}

                    {!loading && !error && queue.length === 0 && (
                        <div className="empty-state">
                            <div className="empty-icon">🎉</div>
                            <h3>Queue is empty!</h3>
                            <p>No {statusFilter} episodes to review at this time.</p>
                        </div>
                    )}

                    {!loading && !error && queue.length > 0 && (
                        <div className="queue-list">
                            {queue.map((item) => (
                                <div key={item.episode.id} className="queue-item">
                                    <div className="item-main">
                                        {/* Cover Art */}
                                        <div className="item-cover">
                                            {item.episode.coverArtURL ? (
                                                <img
                                                    src={item.episode.coverArtURL}
                                                    alt={item.episode.title}
                                                    className="cover-image"
                                                />
                                            ) : (
                                                <div className="cover-placeholder">
                                                    {categoryInfo[item.episode.category]?.icon || '🎙️'}
                                                </div>
                                            )}
                                        </div>

                                        {/* Episode Info */}
                                        <div className="item-info">
                                            <div className="item-title-row">
                                                <h3 className="item-title">{item.episode.title}</h3>
                                                <span className="category-badge">
                                                    {categoryInfo[item.episode.category]?.icon}{' '}
                                                    {item.episode.category}
                                                </span>
                                            </div>

                                            <p className="item-description">
                                                {item.episode.description.length > 150
                                                    ? item.episode.description.substring(0, 150) + '...'
                                                    : item.episode.description}
                                            </p>

                                            <div className="item-meta">
                                                <span>👤 {item.episode.creatorName}</span>
                                                <span>•</span>
                                                <span>⏱️ {formatDuration(item.episode.duration)}</span>
                                                <span>•</span>
                                                <span>📅 {formatDate(item.episode.submittedForReviewAt)}</span>
                                                {item.episode.submissionCount && item.episode.submissionCount > 1 && (
                                                    <>
                                                        <span>•</span>
                                                        <span className="resubmission-badge">
                                                            🔄 Resubmission #{item.episode.submissionCount}
                                                        </span>
                                                    </>
                                                )}
                                            </div>

                                            {/* User Stats Badge */}
                                            {getUserStatusBadge(item.userStats)}

                                            {/* Previous Rejection */}
                                            {item.episode.previousRejections && item.episode.previousRejections.length > 0 && (
                                                <div className="previous-rejection">
                                                    <strong>Previous rejection:</strong>{' '}
                                                    {item.episode.previousRejections[item.episode.previousRejections.length - 1].rejectionReason}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="item-actions">
                                        <button
                                            onClick={() => setSelectedItem(item)}
                                            className="action-button view-button"
                                        >
                                            👁️ Review Details
                                        </button>

                                        {statusFilter === 'pending' && (
                                            <>
                                                <button
                                                    onClick={() => handleApprove(item)}
                                                    className="action-button approve-button"
                                                    disabled={actionLoading}
                                                >
                                                    ✅ Approve
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedItem(item);
                                                        setShowRejectModal(true);
                                                    }}
                                                    className="action-button reject-button"
                                                    disabled={actionLoading}
                                                >
                                                    ❌ Reject
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {/* Review Details Modal */}
            {selectedItem && !showRejectModal && (
                <div className="modal-overlay" onClick={() => setSelectedItem(null)}>
                    <div className="modal-content podcast-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>🎙️ Review Episode</h2>
                            <button onClick={() => setSelectedItem(null)} className="close-button">×</button>
                        </div>

                        <div className="modal-body">
                            {/* Episode Details */}
                            <div className="episode-details">
                                <div className="episode-cover-large">
                                    {selectedItem.episode.coverArtURL ? (
                                        <img src={selectedItem.episode.coverArtURL} alt={selectedItem.episode.title} />
                                    ) : (
                                        <div className="cover-placeholder-large">
                                            {categoryInfo[selectedItem.episode.category]?.icon || '🎙️'}
                                        </div>
                                    )}
                                </div>

                                <div className="episode-metadata">
                                    <h3>{selectedItem.episode.title}</h3>
                                    <p className="episode-description-full">{selectedItem.episode.description}</p>

                                    <div className="metadata-grid">
                                        <div><strong>Creator:</strong> {selectedItem.episode.creatorName}</div>
                                        <div><strong>Category:</strong> {selectedItem.episode.category}</div>
                                        <div><strong>Duration:</strong> {formatDuration(selectedItem.episode.duration)}</div>
                                        <div><strong>Submitted:</strong> {formatDate(selectedItem.episode.submittedForReviewAt)}</div>
                                        <div><strong>Profile ID:</strong> {selectedItem.episode.creatorProfileID}</div>
                                        {selectedItem.episode.submissionCount && selectedItem.episode.submissionCount > 1 && (
                                            <div><strong>Submissions:</strong> {selectedItem.episode.submissionCount}</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Audio Player */}
                            {selectedItem.episode.finalAudioURL && (
                                <div className="audio-player-section">
                                    <h4>🔊 Audio Review</h4>
                                    <audio
                                        ref={audioRef}
                                        controls
                                        className="audio-player"
                                        src={selectedItem.episode.finalAudioURL}
                                    >
                                        Your browser does not support the audio element.
                                    </audio>
                                </div>
                            )}

                            {/* User History */}
                            {selectedItem.userStats && (
                                <div className="user-history-section">
                                    <h4>📊 Creator History</h4>
                                    <div className="user-stats-grid">
                                        <div>
                                            <strong>Total Episodes:</strong> {selectedItem.userStats.totalEpisodes}
                                        </div>
                                        <div>
                                            <strong>Approved:</strong> {selectedItem.userStats.approvedCount}
                                        </div>
                                        <div>
                                            <strong>Rejected:</strong> {selectedItem.userStats.rejectedCount}
                                        </div>
                                        <div>
                                            <strong>Removed:</strong> {selectedItem.userStats.removedCount}
                                        </div>
                                        <div>
                                            <strong>Approval Rate:</strong> {Math.round(selectedItem.userStats.approvalRate * 100)}%
                                        </div>
                                        {selectedItem.userStats.firstEpisodeDate && (
                                            <div>
                                                <strong>First Episode:</strong>{' '}
                                                {selectedItem.userStats.firstEpisodeDate.toLocaleDateString()}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Previous Rejections */}
                            {selectedItem.episode.previousRejections && selectedItem.episode.previousRejections.length > 0 && (
                                <div className="previous-rejections-section">
                                    <h4>📝 Previous Rejections</h4>
                                    {selectedItem.episode.previousRejections.map((rejection, index) => (
                                        <div key={index} className="rejection-item">
                                            <div><strong>Date:</strong> {new Date(rejection.rejectedAt).toLocaleDateString()}</div>
                                            <div><strong>Category:</strong> {rejection.rejectionCategory}</div>
                                            <div><strong>Reason:</strong> {rejection.rejectionReason}</div>
                                            <div><strong>Reviewed by:</strong> {rejection.reviewedBy}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Actions */}
                            <div className="modal-actions">
                                <button
                                    onClick={() => handleApprove(selectedItem)}
                                    className="modal-button approve"
                                    disabled={actionLoading}
                                >
                                    ✅ Approve
                                </button>
                                <button
                                    onClick={() => setShowRejectModal(true)}
                                    className="modal-button reject"
                                    disabled={actionLoading}
                                >
                                    ❌ Reject with Feedback
                                </button>
                                <button
                                    onClick={() => handleFlagForSeniorReview(selectedItem)}
                                    className="modal-button flag"
                                    disabled={actionLoading}
                                >
                                    🚩 Flag for Senior Review
                                </button>
                                <button
                                    onClick={() => handleRemove(selectedItem)}
                                    className="modal-button remove"
                                    disabled={actionLoading}
                                >
                                    🗑️ Remove (Severe Violation)
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {selectedItem && showRejectModal && (
                <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
                    <div className="modal-content reject-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>❌ Reject Episode</h2>
                            <button onClick={() => setShowRejectModal(false)} className="close-button">×</button>
                        </div>

                        <div className="modal-body">
                            <h3>{selectedItem.episode.title}</h3>
                            <p>by {selectedItem.episode.creatorName}</p>

                            <div className="rejection-form">
                                <div className="form-group">
                                    <label><strong>Rejection Category:</strong></label>
                                    <select
                                        value={rejectionCategory}
                                        onChange={(e) => setRejectionCategory(e.target.value as RejectionCategory)}
                                        className="rejection-select"
                                    >
                                        <option value="inappropriate_language">Inappropriate Language</option>
                                        <option value="off_topic_content">Off-Topic Content</option>
                                        <option value="safety_concerns">Safety Concerns</option>
                                        <option value="quality_issues">Quality Issues</option>
                                        <option value="metadata_issues">Metadata Issues</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label><strong>Feedback for User:</strong></label>
                                    <textarea
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        className="rejection-textarea"
                                        rows={6}
                                        placeholder="Provide specific, constructive feedback..."
                                    />
                                    <p className="helper-text">
                                        💡 Tip: Be specific and constructive. The user will see this message.
                                    </p>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button
                                    onClick={() => handleReject(selectedItem)}
                                    className="modal-button reject"
                                    disabled={actionLoading || !rejectionReason.trim()}
                                >
                                    ❌ Send Rejection
                                </button>
                                <button
                                    onClick={() => setShowRejectModal(false)}
                                    className="modal-button cancel"
                                    disabled={actionLoading}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
